import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth } from "@/hooks/useAuth";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminReportGate, reportGet, money } from "@/components/reports/report-primitives";

const REPORTS = [
  { href: "/reports/operations", title: "Operations",
    blurb: "Quiet work by who it is waiting on, unassigned orders, age, and rework." },
  { href: "/reports/sales-by-bucket", title: "Sales by Bucket",
    blurb: "Units and service value for Earl's three buckets, by invoice stage.", flag: true },
  { href: "/reports/documentation", title: "Documentation & staff",
    blurb: "Note coverage per owner, with the base rate to compare against." },
  { href: "/reports/billing", title: "Billing",
    blurb: "Current AR aging, uninvoiced finished work, and billing exceptions." },
  { href: "/reports/compliance", title: "Compliance",
    blurb: "Work that finished with loose ends." },
];

interface Attention {
  stalledWaitingOnUs: number;
  stalledWaitingOnClient: number;
  stalledWaitingOnAgency: number;
  stalledWaitingOnPayment: number;
  stalledTotal: number;
  unassigned: number;
  openAged120: number;
  completedUninvoiced: number;
  ar90PlusGross: number;
  outstandingDocs31Plus: number;
}

export default function ReportsPage() {
  usePageTitle("Reports");
  const { user } = useAuth();
  const attention = useQuery<{ rows: Attention[] }>({
    queryKey: ["/api/reports/attention"],
    queryFn: () => reportGet("/api/reports/attention"),
    enabled: user?.role === "admin",
  });
  const s = attention.data?.rows[0];

  return (
    <AdminReportGate>
      <ResponsiveLayout title="Reports" subtitle="What needs attention, then the library">
        <h2 className="text-xl font-medium mb-1">Needs attention</h2>
        <p className="text-sm text-gray-500 mb-4">
          Snapshot of work that is stuck or leaking. Each card opens the list.
        </p>
        {attention.isLoading && <p className="text-sm text-gray-500 mb-6">Loading attention counts…</p>}
        {attention.error && <p className="text-sm text-red-700 mb-6">{(attention.error as Error).message}</p>}
        {s && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
            <AttentionCard href="/reports/operations?waitingOn=us" label="Quiet — waiting on us"
              value={s.stalledWaitingOnUs} hint="No status change or note in 14+ days, and the next move is ours" alert={s.stalledWaitingOnUs > 0} />
            <AttentionCard href="/reports/operations#unassigned" label="Unassigned open work"
              value={s.unassigned} hint="On the board with no owner" alert={s.unassigned > 0} />
            <AttentionCard href="/reports/operations#aged" label="Open 120+ days"
              value={s.openAged120} hint="Age, not inactivity — includes work waiting on FMCSA" />
            <AttentionCard href="/reports/compliance#docs-31" label="Doc requests 31+ days"
              value={s.outstandingDocs31Plus} hint="Required documents still outstanding" alert={s.outstandingDocs31Plus > 0} />
            <AttentionCard href="/reports/billing#uninvoiced" label="Finished, never invoiced"
              value={s.completedUninvoiced} hint="Work complete or closed with no live invoice" alert={s.completedUninvoiced > 0} />
            <AttentionCard href="/reports/billing#ar-90" label="AR 90+ days"
              value={money(Math.round(s.ar90PlusGross * 100))} hint="Gross balance on chaseable invoices" alert={s.ar90PlusGross > 0} />
          </div>
        )}

        <h2 className="text-xl font-medium mb-4">All reports</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REPORTS.map(r => (
            <Link key={r.href} href={r.href}>
              <Card className="hover:border-primary/40 hover:shadow-sm transition cursor-pointer h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    {r.flag && <span className="mr-1">⭐</span>}{r.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500">{r.blurb}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </ResponsiveLayout>
    </AdminReportGate>
  );
}

function AttentionCard({
  href, label, value, hint, alert,
}: { href: string; label: string; value: number | string; hint: string; alert?: boolean }) {
  const n = typeof value === "number" ? value : null;
  return (
    <Link href={href}>
      <Card className={`hover:border-primary/40 hover:shadow-sm transition cursor-pointer h-full ${
        alert ? "border-amber-300 bg-amber-50/60" : ""}`}>
        <CardHeader className="pb-1">
          <CardDescription>{label}</CardDescription>
          <CardTitle className={`text-2xl tabular-nums ${alert && n !== 0 ? "text-amber-900" : ""}`}>
            {value}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-gray-500">{hint}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
